/**
 * Withdrawal Table Component
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { CheckCircle, XCircle, RefreshCw, Eye, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  processing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  refunded: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const WithdrawalTable = ({ 
  withdrawals, 
  loading, 
  onApprove, 
  onCancel, 
  onRefund,
  onViewDetails 
}) => {
  const handleCancel = (withdrawal) => {
    const reason = prompt('Enter cancellation reason:');
    if (reason) onCancel(withdrawal.id, reason);
  };

  const handleRefund = (withdrawal) => {
    const reason = prompt('Enter refund reason:');
    if (reason) onRefund(withdrawal.id, reason);
  };

  return (
    <Card className="bg-gray-800/50 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          Withdrawal Requests
          {loading && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700 hover:bg-transparent">
                <TableHead className="text-gray-400">ID</TableHead>
                <TableHead className="text-gray-400">User</TableHead>
                <TableHead className="text-gray-400">Amount</TableHead>
                <TableHead className="text-gray-400">Fee</TableHead>
                <TableHead className="text-gray-400">Net</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Date</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withdrawals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                    No withdrawals found
                  </TableCell>
                </TableRow>
              ) : (
                withdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.id} className="border-gray-700 hover:bg-gray-800/50">
                    <TableCell className="text-white font-mono text-sm">
                      {withdrawal.id?.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {withdrawal.user_id?.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-white font-semibold">
                      {formatCurrency(withdrawal.amount)}
                    </TableCell>
                    <TableCell className="text-red-400">
                      -{formatCurrency(withdrawal.fee)}
                    </TableCell>
                    <TableCell className="text-green-400 font-semibold">
                      {formatCurrency(withdrawal.net_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[withdrawal.status]} border`}>
                        {withdrawal.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {formatDate(withdrawal.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700">
                          <DropdownMenuItem 
                            className="text-gray-300 hover:bg-gray-700"
                            onClick={() => onViewDetails?.(withdrawal)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          
                          {withdrawal.status === 'pending' && (
                            <>
                              <DropdownMenuItem 
                                className="text-green-400 hover:bg-gray-700"
                                onClick={() => onApprove(withdrawal.id)}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-400 hover:bg-gray-700"
                                onClick={() => handleCancel(withdrawal)}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          {['approved', 'processing'].includes(withdrawal.status) && (
                            <DropdownMenuItem 
                              className="text-orange-400 hover:bg-gray-700"
                              onClick={() => handleRefund(withdrawal)}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Refund
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default WithdrawalTable;
